<template>
  <div ref="root" class="wy-mention-editor" @click="handleRootClick">
    <div
      ref="editor"
      class="wy-mention-editor__input"
      :class="{ 'is-disabled': disabled, 'is-multiline': multiline }"
      contenteditable="true"
      :data-placeholder="placeholder"
      @input="handleInput"
      @paste="handlePaste"
      @keydown="handleKeydown"
      @focus="handleFocus"
      @blur="handleBlur"
    ></div>

    <div
      v-if="isOpen"
      class="wy-mention-editor__menu"
      :style="menuStyle"
      @mousedown.prevent
    >
      <div
        v-for="(metadata, index) in filteredMetadatas"
        ref="menuItems"
        :key="getItemKey(metadata, index)"
        class="wy-mention-editor__item"
        :class="{ 'is-active': index === highlightedIndex }"
        @mouseenter="highlightedIndex = index"
        @mousedown.prevent="selectItem(metadata)"
      >
        {{ getItemLabel(metadata) }}
      </div>
      <div v-if="filteredMetadatas.length === 0" class="wy-mention-editor__empty">
        No matches
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

export default Vue.extend({
  name: 'WyMentionEditor',

  props: {
    value: {
      type: String,
      default: ''
    },
    metadataList: {
      type: Array,
      default: () => []
    },
    keys: {
      type: Array,
      default: () => ['@']
    },
    placeholder: {
      type: String,
      default: 'Type here...'
    },
    offset: {
      type: [String, Number],
      default: 6
    },
    limit: {
      type: Number,
      default: 8
    },
    maxlength: {
      type: Number,
      default: null
    },
    multiline: {
      type: Boolean,
      default: true
    },
    tagType: {
      type: String,
      default: '',
      validator: (value) => ['', 'success', 'info', 'warning', 'danger'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasMetadataFeatureFlag: {
      type: Boolean,
      default: true
    },
    wrapMenuNavigation: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // Controls the visibility of the suggestion menu that appears after typing a trigger like '@'.
      isOpen: false,
      // The current search text after the trigger character, used to filter metadataList.
      query: '',
      // Stores which trigger key opened the menu. This matters if multiple trigger keys are supported.
      activeKey: null,
      // Tracks keyboard navigation inside the suggestion dropdown.
      highlightedIndex: 0,
      // Remembers the exact DOM range that should be replaced when a suggestion is selected.
      triggerRange: null,
      menuStyle: {
        top: '0px',
        left: '0px'
      },
      // Prevents the value watcher from re-rendering the editor while we are already applying an external value.
      isApplyingExternalValue: false
    };
  },

  computed: {
    normalizedOffset() {
      return Number(this.offset) || 0;
    },

    mentionTagClassName() {
      return `wy-mention-editor__tag el-tag el-tag--${this.tagType}`;
    },

    filteredMetadatas() {
      const normalizedQuery = this.query.toLowerCase();
      const filtered = this.metadataList.filter((metadata) => {
        if (!normalizedQuery) {
          return true;
        }

        return this.getSearchTokens(metadata).some((token) => token.includes(normalizedQuery));
      });

      return filtered.slice(0, this.limit);
    }
  },

  watch: {
    value(nextValue) {
      if (this.isApplyingExternalValue) {
        return;
      }

      // Keep the contenteditable DOM in sync with the parent value, but avoid unnecessary re-renders
      // when the current DOM already serializes to the same string.
      if (nextValue !== this.serializeContent()) {
        this.renderPlainText(nextValue);
      }
    },
    highlightedIndex() {
      if (!this.isOpen) {
        return;
      }

      this.$nextTick(() => {
        this.scrollHighlightedItemIntoView();
      });
    }
  },

  mounted() {
    // Render the initial plain-text/model value into the contenteditable DOM.
    this.renderPlainText(this.value);
    // Global click handling is used to close the menu when the user clicks outside the editor.
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    handleRootClick() {
      if (this.disabled || !this.$refs.editor) {
        return;
      }

      // Clicking anywhere inside the wrapper should behave like clicking the real input surface.
      this.$refs.editor.focus();
      this.ensureCaretInEditableTextNode();
      this.refreshMentionState();
    },

    handleFocus() {
      this.$emit('focus');
      this.ensureCaretInEditableTextNode();
      this.refreshMentionState();
    },

    handleBlur() {
      this.$emit('blur');
      window.setTimeout(() => {
        this.closeMenu();
      }, 100);
    },

    handleInput() {
      // In single-line mode, strip DOM structures that browsers may inject into contenteditable
      // so serialization stays predictable.
      if (!this.multiline) {
        this.normalizeSingleLineContent();
      }

      // Emit the latest serialized string to the parent, then recompute whether the mention menu
      // should open, close, or change its query.
      this.emitValue();
      this.refreshMentionState();
    },

    scrollHighlightedItemIntoView() {
      const menuItems = this.$refs.menuItems;
      const activeItem = Array.isArray(menuItems)
        ? menuItems[this.highlightedIndex]
        : menuItems;

      if (activeItem && typeof activeItem.scrollIntoView === 'function') {
        activeItem.scrollIntoView({ block: 'nearest' });
      }
    },

    handlePaste(event) {
      if (this.disabled) {
        event.preventDefault();
        return;
      }

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) {
        return;
      }

      event.preventDefault();

      const selection = window.getSelection();
      const editor = this.$refs.editor;
      if (!selection || !selection.rangeCount || !editor) {
        return;
      }

      let pastedText = clipboardData.getData('text/plain') || '';
      if (!this.multiline) {
        pastedText = pastedText.replace(/[\r\n]+/g, ' ');
      }

      if (!pastedText) {
        return;
      }

      const currentValue = this.serializeContent();
      const selectedTextLength = this.getSerializedRangeLength(selection.getRangeAt(0));
      const remainingLength = this.maxlength == null
        ? pastedText.length
        : Math.max(this.maxlength - (currentValue.length - selectedTextLength), 0);

      if (remainingLength === 0) {
        this.emitValue();
        this.refreshMentionState();
        return;
      }

      const insertedText = pastedText.slice(0, remainingLength);
      const range = selection.getRangeAt(0);
      range.deleteContents();

      const textNode = document.createTextNode(insertedText);
      range.insertNode(textNode);

      const caretRange = document.createRange();
      caretRange.setStart(textNode, textNode.textContent.length);
      caretRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caretRange);

      if (!this.multiline) {
        this.normalizeSingleLineContent();
      }

      this.emitValue();
      this.refreshMentionState();
    },

    handleKeydown(event) {
      if (this.disabled) {
        event.preventDefault();
        return;
      }

      if (event.key === 'Backspace' && this.handleMentionBackspace(event)) {
        return;
      }

      // While the suggestion menu is open, arrow keys and Enter/Tab are re-routed to menu behavior
      // instead of editing the text directly.
      if (this.isOpen) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (this.filteredMetadatas.length > 0) {
            if (this.wrapMenuNavigation) {
              this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredMetadatas.length;
            }
            else if (this.highlightedIndex < this.filteredMetadatas.length - 1) {
              this.highlightedIndex += 1;
            }
          }
          return;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (this.filteredMetadatas.length > 0) {
            if (this.wrapMenuNavigation) {
              this.highlightedIndex = (this.highlightedIndex - 1 + this.filteredMetadatas.length) % this.filteredMetadatas.length;
            }
            else if (this.highlightedIndex > 0) {
              this.highlightedIndex -= 1;
            }
          }
          return;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          if (this.filteredMetadatas[this.highlightedIndex]) {
            event.preventDefault();
            this.selectItem(this.filteredMetadatas[this.highlightedIndex]);
          }
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeMenu();
          return;
        }
      }

      if (this.shouldPreventInputForMaxlength(event)) {
        event.preventDefault();
        return;
      }

      if (!this.multiline && event.key === 'Enter') {
        event.preventDefault();
        return;
      }

      this.$nextTick(() => {
        this.refreshMentionState();
      });
    },

    handleMentionBackspace(event) {
      // Mention chips are treated as atomic units. If the caret is positioned just after a chip,
      // Backspace removes the whole chip instead of partially deleting its label.
      const targetNode = this.getBackwardDeleteMentionTarget();
      if (!this.isMentionTagElement(targetNode)) {
        return false;
      }

      event.preventDefault();
      this.removeMentionNode(targetNode);
      this.closeMenu();
      return true;
    },

    getBackwardDeleteMentionTarget() {
      const selection = window.getSelection();
      const editor = this.$refs.editor;

      if (!selection || !selection.rangeCount || !selection.isCollapsed || !editor) {
        return null;
      }

      // The editor supports caret positions inside either text nodes or the root editable element,
      // so this method normalizes both cases into "is there a mention chip immediately before me?".
      const range = selection.getRangeAt(0);

      if (range.startContainer.nodeType === Node.TEXT_NODE) {
        const textNode = range.startContainer;

        if (range.startOffset === 0) {
          return this.findPreviousMentionTag(textNode);
        }

        return null;
      }

      if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
        const childBeforeCaret = range.startContainer.childNodes[range.startOffset - 1] || null;
        if (this.isMentionTagElement(childBeforeCaret)) {
          return childBeforeCaret;
        }

        if (childBeforeCaret && childBeforeCaret.nodeType === Node.TEXT_NODE) {
          if (childBeforeCaret.textContent.length === 0) {
            return this.findPreviousMentionTag(childBeforeCaret);
          }
        }
      }

      return null;
    },

    findPreviousMentionTag(node) {
      let current = node ? node.previousSibling : null;

      while (current) {
        if (this.isMentionTagElement(current)) {
          return current;
        }

        if (current.nodeType === Node.TEXT_NODE && current.textContent.length === 0) {
          current = current.previousSibling;
          continue;
        }

        break;
      }

      return null;
    },

    handleOutsideClick(event) {
      if (!this.$refs.root || this.$refs.root.contains(event.target)) {
        return;
      }

      this.closeMenu();
    },

    refreshMentionState() {
      const selection = window.getSelection();
      const editor = this.$refs.editor;

      if (!this.hasMetadataFeatureFlag) {
        this.closeMenu();
        return;
      }

      if (!editor || !selection || !selection.rangeCount || !editor.contains(selection.anchorNode)) {
        this.closeMenu();
        return;
      }

      if (this.hasMentionTag()) {
        this.closeMenu();
        return;
      }

      // Convert the browser selection into a text-node-based view so trigger detection can inspect
      // the text directly before the caret.
      const normalizedSelection = this.getNormalizedSelection(selection);
      if (!normalizedSelection) {
        this.closeMenu();
        return;
      }

      // A trigger is something like "@scr" immediately before the caret. If found, the menu is
      // opened and remembers the range that will later be replaced by the selected mention chip.
      const trigger = this.findTrigger(normalizedSelection.textBeforeCaret);
      if (!trigger) {
        this.closeMenu();
        return;
      }

      this.activeKey = trigger.key;
      this.query = trigger.query;
      this.highlightedIndex = 0;
      this.triggerRange = this.createTriggerRange(normalizedSelection, trigger);
      this.isOpen = true;

      this.$emit('search', {
        key: this.activeKey,
        query: this.query
      });

      this.updateMenuPosition();
    },

    getNormalizedSelection(selection) {
      const range = selection.getRangeAt(0);
      let container = range.startContainer;
      let offset = range.startOffset;

      // Browsers sometimes report a caret position on the editor element instead of a text node.
      // For mention parsing we need a text node so we can examine the text before the caret.
      if (container.nodeType === Node.ELEMENT_NODE) {
        const childNode = container.childNodes[offset - 1] || container.childNodes[offset] || null;
        if (childNode && childNode.nodeType === Node.TEXT_NODE) {
          container = childNode;
          offset = childNode.textContent.length;
        } else {
          return null;
        }
      }

      if (!container || container.nodeType !== Node.TEXT_NODE) {
        return null;
      }

      return {
        node: container,
        offset,
        textBeforeCaret: container.textContent.slice(0, offset)
      };
    },

    findTrigger(textBeforeCaret) {
      let bestMatch = null;

      // Find the nearest valid trigger among all supported keys. The nearest one wins because it is
      // the active mention currently being typed.
      this.keys.forEach((key) => {
        const positions = this.findAllTriggerPositions(textBeforeCaret, key);
        if (positions.length === 0) {
          return;
        }

        const start = positions[positions.length - 1];
        const rawQuery = textBeforeCaret.slice(start + key.length);
        if (/\s/.test(rawQuery)) {
          return;
        }

        if (!bestMatch || start > bestMatch.start) {
          bestMatch = {
            key,
            start,
            query: rawQuery
          };
        }
      });

      return bestMatch;
    },

    findAllTriggerPositions(text, key) {
      const positions = [];
      let fromIndex = 0;

      // Treat the trigger key itself as valid anywhere in the text so typing '@' immediately opens
      // the menu instead of requiring a preceding whitespace character.
      while (fromIndex < text.length) {
        const start = text.indexOf(key, fromIndex);
        if (start === -1) {
          break;
        }

        positions.push(start);

        fromIndex = start + key.length;
      }

      return positions;
    },

    createTriggerRange(selectionState, trigger) {
      const range = document.createRange();
      range.setStart(selectionState.node, trigger.start);
      range.setEnd(selectionState.node, selectionState.offset);
      return range;
    },

    updateMenuPosition() {
      this.$nextTick(() => {
        if (!this.triggerRange || !this.$refs.root || !this.$refs.editor) {
          return;
        }

        // Position the menu relative to the trigger text so the dropdown appears close to the caret.
        const rangeRect = this.triggerRange.getBoundingClientRect();
        const editorRect = this.$refs.editor.getBoundingClientRect();
        const rootRect = this.$refs.root.getBoundingClientRect();
        const fallbackLeft = editorRect.left - rootRect.left + 12;
        const left = rangeRect.left ? rangeRect.left - rootRect.left : fallbackLeft;
        const top = (rangeRect.bottom ? rangeRect.bottom - rootRect.top : editorRect.top - rootRect.top) + this.normalizedOffset;

        this.menuStyle = {
          left: `${left}px`,
          top: `${top}px`
        };
      });
    },

    selectItem(metadata) {
      const selection = window.getSelection();
      const editor = this.$refs.editor;

      if (!selection || !selection.rangeCount || !this.triggerRange || !editor) {
        return;
      }

      // Determine whether the character after the caret is already whitespace so we do not create
      // an extra space after inserting the mention chip.
      const currentRange = selection.getRangeAt(0);
      const nextCharacter = currentRange.startContainer.nodeType === Node.TEXT_NODE
        ? currentRange.startContainer.textContent.slice(currentRange.startOffset, currentRange.startOffset + 1)
        : '';
      const fragment = document.createDocumentFragment();
      const mentionNode = this.createMentionNode(metadata);
      const trailingTextNode = document.createTextNode(nextCharacter && /\s/.test(nextCharacter) ? '' : ' ');

      // Replace the typed trigger text (for example "@scr") with an atomic, non-editable chip node.
      this.triggerRange.deleteContents();
      fragment.appendChild(mentionNode);
      fragment.appendChild(trailingTextNode);
      this.triggerRange.insertNode(fragment);

      // Move the caret after the inserted chip so the user can continue typing naturally.
      const caretRange = document.createRange();
      caretRange.setStart(trailingTextNode, 1);
      caretRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caretRange);
      editor.normalize();

      const label = this.getItemInsertValue(metadata);
      // Keep the parent model in sync and expose a richer event describing which metadata item was applied.
      this.emitValue();
      this.$emit('apply', {
        item: metadata,
        key: this.activeKey,
        value: this.serializeContent(),
        label
      });

      this.closeMenu();
      this.refreshMentionState();
    },

    createMentionNode(metadata) {
      const wrapper = document.createElement('span');
      const mentionKey = this.activeKey || this.keys[0];
      // Mention chips are rendered as non-editable inline elements. The visible text is the label,
      // while dataset fields preserve metadata needed for serialization and removal events.
      wrapper.className = this.mentionTagClassName;
      wrapper.contentEditable = 'false';
      wrapper.dataset.key = this.getItemKey(metadata, 0);
      wrapper.dataset.label = this.getItemLabel(metadata);
      wrapper.dataset.value = `${mentionKey}${this.getItemInsertValue(metadata)}`;
      wrapper.style.display = 'inline';
      wrapper.style.alignItems = 'center';
      wrapper.style.verticalAlign = 'middle';
      // wrapper.style.margin = '6px 10px';
      wrapper.style.padding = '6px 10px';

      const labelNode = document.createElement('span');
      labelNode.className = 'wy-mention-editor__tag-label';
      labelNode.textContent = `${mentionKey}${this.getItemLabel(metadata)}`;

      const closeNode = document.createElement('span');
      closeNode.className = 'wy-mention-editor__tag-close el-tag__close el-icon-close';
      closeNode.addEventListener('mousedown', (event) => {
        event.preventDefault();
      });
      closeNode.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.removeMentionNode(wrapper);
      });

      wrapper.appendChild(labelNode);
      wrapper.appendChild(closeNode);
      return wrapper;
    },

    removeMentionNode(node) {
      const parent = node.parentNode;
      if (!parent) {
        return;
      }

      const label = node.dataset.label || '';
      const key = node.dataset.key || '';
      // Keep or create a text node after the removed chip so the caret has a valid landing spot.
      const nextTextNode = node.nextSibling && node.nextSibling.nodeType === Node.TEXT_NODE
        ? node.nextSibling
        : document.createTextNode('');

      if (!node.nextSibling) {
        parent.insertBefore(nextTextNode, node.nextSibling);
      }

      // Removing the chip updates the editor value and notifies the parent that the mention metadata changed.
      parent.removeChild(node);
      this.placeCaretAtTextNode(nextTextNode, 0);
      this.emitValue();
      this.$emit('remove-mention', {
        key,
        label
      });
    },

    placeCaretAtTextNode(node, offset) {
      const selection = window.getSelection();
      if (!selection) {
        return;
      }

      // Contenteditable caret placement is done through DOM Range/Selection APIs.
      const range = document.createRange();
      range.setStart(node, Math.min(offset, node.textContent.length));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
    },

    ensureCaretInEditableTextNode() {
      const editor = this.$refs.editor;
      const selection = window.getSelection();

      if (!editor || !selection) {
        return;
      }

      // Some interactions leave the caret on the wrapper element or after a non-text node. This
      // helper guarantees a real text node exists so later typing and trigger detection behave well.
      if (selection.rangeCount && editor.contains(selection.anchorNode) && selection.anchorNode.nodeType === Node.TEXT_NODE) {
        return;
      }

      const textNode = this.ensureTrailingTextNode();
      this.placeCaretAtTextNode(textNode, textNode.textContent.length);
    },

    ensureTrailingTextNode() {
      const editor = this.$refs.editor;
      if (!editor) {
        return document.createTextNode('');
      }

      // The editor always prefers ending with a text node so the caret can be placed after mention chips.
      const lastNode = editor.lastChild;
      if (lastNode && lastNode.nodeType === Node.TEXT_NODE) {
        return lastNode;
      }

      const textNode = document.createTextNode('');
      editor.appendChild(textNode);
      return textNode;
    },

    emitValue() {
      // The component's public value is always derived from the DOM through serialization rather than
      // trusting incremental text bookkeeping.
      const value = this.serializeContent();
      this.$emit('input', value);
      this.$emit('change', value);
    },

    shouldPreventInputForMaxlength(event) {
      if (!this.maxlength || event.ctrlKey || event.metaKey || event.altKey) {
        return false;
      }

      if (!this.isEditingKey(event.key)) {
        return false;
      }

      return this.serializeContent().length >= this.maxlength;
    },

    isEditingKey(key) {
      if (!key) {
        return false;
      }

      return key.length === 1;
    },

    normalizeSingleLineContent() {
      const editor = this.$refs.editor;
      if (!editor) {
        return;
      }

      // Browsers may inject <br> or nested elements into contenteditable. In single-line mode we
      // flatten those back into plain text nodes so serialization stays simple and predictable.
      Array.from(editor.querySelectorAll('br')).forEach((node) => {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      });

      Array.from(editor.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && !this.isMentionTagElement(node)) {
          const textNode = document.createTextNode(node.textContent || '');
          editor.replaceChild(textNode, node);
        }
      });
    },

    isMentionTagElement(node) {
      return Boolean(
        node &&
        node.nodeType === Node.ELEMENT_NODE &&
        node.classList &&
        node.classList.contains('wy-mention-editor__tag')
      );
    },

    getSerializedRangeLength(range) {
      if (!range) {
        return 0;
      }

      const fragment = range.cloneContents();
      return Array.from(fragment.childNodes).map((node) => this.serializeNode(node)).join('').length;
    },

    serializeContent() {
      const editor = this.$refs.editor;
      if (!editor) {
        return '';
      }

      // Walk each top-level child node and convert the mixed DOM structure back into the plain string
      // contract used by the parent component.
      return Array.from(editor.childNodes).map((node) => this.serializeNode(node)).join('');
    },

    serializeNode(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.dataset && node.dataset.value) {
          // Mention chips serialize from dataset.value instead of visible text so the parent can keep
          // a stable representation independent of the rendered DOM structure.
          return `${node.dataset.value}`;
        }

        return Array.from(node.childNodes).map((childNode) => this.serializeNode(childNode)).join('');
      }

      return '';
    },

    renderPlainText(value) {
      const editor = this.$refs.editor;
      if (!editor) {
        return;
      }

      // Rebuild the contenteditable DOM from the string value owned by the parent.
      this.isApplyingExternalValue = true;
      editor.innerHTML = '';
      const content = value || '';
      // This renderer currently supports reconstructing one mention chip by finding the first visible
      // @label pattern that matches an item from metadataList.
      const matchedMetadata = this.metadataList.find((metadata) => content.includes(`${this.keys[0]}${this.getItemLabel(metadata)}`));

      if (!matchedMetadata) {
        editor.appendChild(document.createTextNode(content));
        this.isApplyingExternalValue = false;
        return;
      }

      // Split the incoming string into text before the mention, the mention chip itself, and the text
      // after it, then rebuild the DOM in that order.
      const mentionText = `${this.keys[0]}${this.getItemLabel(matchedMetadata)}`;
      const mentionIndex = content.indexOf(mentionText);
      const beforeText = content.slice(0, mentionIndex);
      const afterText = content.slice(mentionIndex + mentionText.length);

      if (beforeText) {
        editor.appendChild(document.createTextNode(beforeText));
      }

      editor.appendChild(this.createMentionNode(matchedMetadata));
      editor.appendChild(document.createTextNode(afterText));
      this.isApplyingExternalValue = false;
    },

    clear() {
      this.renderPlainText('');
      this.emitValue();
      this.closeMenu();
    },

    focus() {
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
    },

    hasMentionTag() {
      return Boolean(this.$refs.editor && this.$refs.editor.querySelector('.wy-mention-editor__tag'));
    },

    getItemKey(metadata, index) {
      return metadata.key || index;
    },

    getItemLabel(metadata) {
      return metadata.label || '';
    },

    getItemInsertValue(metadata) {
      return metadata.label || '';
    },

    getSearchTokens(metadata) {
      return [metadata.key, metadata.label]
        .filter(Boolean)
        .map((token) => String(token).toLowerCase());
    },

    closeMenu() {
      this.isOpen = false;
      this.query = '';
      this.activeKey = null;
      this.highlightedIndex = 0;
      this.triggerRange = null;
    }
  }
});
</script>

<style lang="scss" scoped>
.wy-mention-editor {
  position: relative;
  width: 100%;
}

.wy-mention-editor__input {
  min-height: 40px;
  height: 40px;
  width: 100%;
  padding: 0px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  line-height: 38px;
  outline: none;
  word-break: break-word;
  white-space: pre;
  overflow-x: hidden;
  overflow-y: hidden;
  transition: border-color 0.2s;

  &.is-multiline {
    height: auto;
    padding: 8px 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-x: hidden;
    overflow-y: auto;
  }

  &:focus {
    border-color: #409eff;
  }

  &.is-disabled {
    background: #f5f7fa;
    color: #c0c4cc;
    cursor: not-allowed;
    pointer-events: none;
  }

  &:empty::before {
    content: attr(data-placeholder);
    color: #c0c4cc;
  }
}

.wy-mention-editor__menu {
  position: absolute;
  z-index: 2000;
  min-width: 180px;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.wy-mention-editor__item {
  padding: 10px 12px;
  color: #606266;
  cursor: pointer;

  &:hover,
  &.is-active {
    background: #f5f7fa;
    color: #409eff;
  }
}

.wy-mention-editor__empty {
  padding: 12px;
  color: #909399;
}

.wy-mention-editor__tag {
  display: inline-flex;
  align-items: center;
  height: 24px;
  margin: 0;
  padding: 0 8px;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  background-color: #ecf5ff;
  color: #409eff;
  font-size: 12px;
  line-height: 22px;
  vertical-align: middle;
  user-select: none;
}

.el-tag {
  display: inline !important;
  padding: 6px 10px !important;
  line-height: 0px !important;
}

.wy-mention-editor__tag-label {
  display: inline-block;
}

.wy-mention-editor__tag-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-left: 6px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;

  &:hover {
    background: rgba(64, 158, 255, 0.12);
  }
}
</style>
